import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Upload, Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (u && u.startsWith("/api") ? `${BACKEND_URL}${u}` : u);

const FIELDS = [
  ["name", "Nombre / Razón social"],
  ["cif", "CIF/NIF"],
  ["phone", "Teléfono"],
  ["email", "Email"],
  ["address", "Dirección"],
  ["postal_code", "Código postal"],
  ["city", "Ciudad"],
  ["province", "Provincia"],
  ["country", "País"],
  ["iban", "IBAN"],
  ["logo_url", "URL del logo (opcional)"],
];

export default function PerfilPage() {
  const [p, setP] = useState(null);
  const fileRef = useRef(null);
  const importRef = useRef(null);

  const load = async () => {
    try { const r = await api.get("/profile"); setP(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put("/profile", p); toast.success("Perfil actualizado"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((x) => ({ ...x, logo_url: r.data.logo_url }));
      toast.success("Logo actualizado");
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  if (!p) return <div className="text-zinc-500">Cargando…</div>;

  return (
    <div data-testid="perfil-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="section-title">Mi Perfil</h1>
          <p className="text-sm text-zinc-500 mt-1">Estos datos aparecen como emisor en tus presupuestos y facturas.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await api.get("/export", { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
                a.href = url; a.download = `crm_export_${ts}.json`;
                a.click(); URL.revokeObjectURL(url);
              } catch (e) { toast.error("Error al exportar"); }
            }}
            data-testid="btn-export"
          >
            <Download size={14} className="mr-1" />Exportar todo
          </Button>
          <Button
            variant="outline"
            onClick={() => importRef.current?.click()}
            data-testid="btn-import"
          >
            <RotateCcw size={14} className="mr-1" />Restaurar backup
          </Button>
          <input
            ref={importRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            data-testid="import-file-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (!confirm(`¿Restaurar backup "${file.name}"? Los registros existentes con el mismo ID se sobreescribirán. Los que no estén en el backup se mantendrán.`)) {
                e.target.value = ""; return;
              }
              const fd = new FormData();
              fd.append("file", file);
              try {
                const r = await api.post("/export/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
                const c = r.data.counts;
                toast.success(
                  `Backup restaurado — perfil, ${c.clients} clientes, ${c.vehicles} vehículos, ` +
                  `${c.quotes} presupuestos, ${c.invoices} facturas, ${c.ai_datasets} datasets IA.`
                );
                load(); // Reload profile
              } catch (err) { toast.error(formatApiError(err)); }
              e.target.value = "";
            }}
          />
          <Button onClick={save} data-testid="btn-save-profile"><Save size={14} className="mr-1" />Guardar</Button>
        </div>
      </div>

      <div className="page-card grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={k === "address" || k === "logo_url" ? "md:col-span-2" : ""}>
            <Label className="text-xs">{label}</Label>
            <Input data-testid={`profile-${k}`} value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} />
          </div>
        ))}
        <div>
          <Label className="text-xs">IVA por defecto (%)</Label>
          <Input type="number" step="0.5" data-testid="profile-vat" value={p.default_vat ?? 21}
                 onChange={(e) => setP({ ...p, default_vat: parseFloat(e.target.value || "0") })} />
        </div>
      </div>

      <div className="mt-6 page-card max-w-3xl">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Logo</div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-upload-logo">
              <Upload size={14} className="mr-1" />Subir logo
            </Button>
          </div>
        </div>
        {p.logo_url
          ? <img src={absUrl(p.logo_url)} alt="logo" className="max-w-[180px]" />
          : <div className="text-sm text-zinc-400">Sin logo. Sube una imagen (PNG, JPG, SVG…).</div>
        }
      </div>
    </div>
  );
}
